/**
 * Költségbontás — havi időszakra projekt + személy szerinti aggregáció.
 *
 * Megmutatja:
 * - Projektenkénti összegek (mennyi költött melyik projektre)
 * - Személyenkénti összegek (kihez tartozik mennyi költség — split + paid_by)
 * - Cég vs magán fizetésű számlák összesítése
 * - Visszatérítendő összegek per user (aki magánból fizetett, de a cég állja)
 */
import { useState } from 'react'
import { ChevronLeft, ChevronRight, PieChart as PieIcon, Wallet, Users, FolderKanban, Download, TrendingUp, Tag, Trophy, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { BarChart, DonutChart } from '@/components/ui/Charts'
import { useBreakdownTrend } from '@/lib/api/hooks'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { CenterSpinner } from '@/components/ui/Spinner'
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table'
import { EmptyState } from '@/components/ui/EmptyState'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useBreakdown } from '@/lib/api/hooks'
import { moneyCompact } from '@/lib/format'

const MONTH_NAMES_HU = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
]

function defaultPeriod(): { year: number; month: number } {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 }
}

/**
 * CSV letöltés — a böngészőben új tabon nyitva. A backend Authorization fejlécet
 * vár, így <a href> helyett fetch + Blob + download trükköt használunk.
 */
async function downloadCsv(companyId: string | null | undefined, year: number, month: number, kind: 'summary' | 'invoices') {
  if (!companyId) return
  const url =
    kind === 'summary'
      ? `/api/v1/breakdown/${companyId}/${year}/${month}.csv`
      : `/api/v1/breakdown/${companyId}/${year}/${month}/invoices.csv`
  const token = localStorage.getItem('adaptivo.szamla.token')
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!res.ok) {
    alert('CSV letöltés sikertelen: ' + res.status)
    return
  }
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = (kind === 'summary' ? 'koltsegbontas' : 'szamlak') + `_${year}_${String(month).padStart(2, '0')}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(a.href), 5000)
}

export function CostBreakdownPage() {
  const { activeCompanyId } = useAuth()
  const [{ year, month }, setPeriod] = useState(() => defaultPeriod())
  const q = useBreakdown(activeCompanyId, year, month)
  const trendQ = useBreakdownTrend(activeCompanyId, 12)

  function shift(delta: number) {
    setPeriod((p) => {
      const d = new Date(p.year, p.month - 1 + delta, 1)
      return { year: d.getFullYear(), month: d.getMonth() + 1 }
    })
  }

  if (!activeCompanyId) {
    return <p className="text-sm text-slate-500">Nincs aktív cég.</p>
  }

  const data = q.data

  return (
    <div>
      <PageHeader
        title={`Költségbontás · ${year}. ${MONTH_NAMES_HU[month - 1]}`}
        subtitle="Projekt + személy szerinti bontás, magán-cég kifizetések, visszatérítendő összegek."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-3.5 w-3.5" />}
              onClick={() => downloadCsv(activeCompanyId, year, month, 'summary')}
            >
              Bontás CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Download className="h-3.5 w-3.5" />}
              onClick={() => downloadCsv(activeCompanyId, year, month, 'invoices')}
            >
              Tételek CSV
            </Button>
            <div className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
              <button
                type="button"
                onClick={() => shift(-1)}
                className="px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Előző hónap"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setPeriod(defaultPeriod())}
                className="px-3 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-slate-800 border-x border-slate-300 dark:border-slate-700"
              >
                Aktuális
              </button>
              <button
                type="button"
                onClick={() => shift(1)}
                className="px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Következő hónap"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        }
      />

      {q.isLoading || !data ? (
        <CenterSpinner />
      ) : data.invoiceCount === 0 ? (
        <EmptyState
          icon={<PieIcon className="h-8 w-8" />}
          title="Nincs számla ebben a hónapban"
          description="Tölts fel vagy szinkronizálj számlákat, majd jelölj felosztást a Költségfelosztás tabon."
        />
      ) : (
        <div className="space-y-5">
          {/* Top summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SummaryCard label="Összes számla" value={String(data.invoiceCount)} sub="db" />
            <SummaryCard label="Összes bruttó" value={moneyCompact(data.totalGross, 'HUF')} sub="" />
            <SummaryCard
              label="Magán-kifizetésű"
              value={moneyCompact(data.byPaidBy.personal.gross, 'HUF')}
              sub={`${data.byPaidBy.personal.count} db`}
              tone="amber"
            />
          </div>

          {/* Visszatérítendő összegek */}
          {data.personalRefund.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <Wallet className="h-4 w-4" /> Visszatérítendő összegek (magán → cég állja)
                  </span>
                </CardTitle>
              </CardHeader>
              <Table>
                <THead>
                  <TR>
                    <TH>Személy</TH>
                    <TH align="right">Visszatérítendő</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.personalRefund.map((r) => (
                    <TR key={r.userId}>
                      <TD>{r.userName ?? '—'}</TD>
                      <TD numeric className="font-semibold">
                        {moneyCompact(r.totalToRefund, 'HUF')}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}

          {/* Projektek */}
          <Card>
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <FolderKanban className="h-4 w-4" /> Projektek
                </span>
              </CardTitle>
            </CardHeader>
            {data.byProject.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">Nincs projekt jelölve.</div>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Projekt kód</TH>
                    <TH align="right">Számla db</TH>
                    <TH align="right">ÁFA</TH>
                    <TH align="right">Bruttó</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.byProject.map((p) => (
                    <TR key={p.projectCode ?? '_none'}>
                      <TD>
                        {p.projectCode ?? (
                          <span className="text-slate-400">— nincs projekt —</span>
                        )}
                      </TD>
                      <TD numeric>{p.count}</TD>
                      <TD numeric>{moneyCompact(p.vat, 'HUF')}</TD>
                      <TD numeric className="font-semibold">
                        {moneyCompact(p.gross, 'HUF')}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>

          {/* Személyek */}
          <Card>
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <Users className="h-4 w-4" /> Személyek (split + paid_by alapján)
                </span>
              </CardTitle>
            </CardHeader>
            {data.byUser.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">Nincs személyhez allokált költség.</div>
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Személy</TH>
                    <TH align="right">Érintett számla db</TH>
                    <TH align="right">Allokált bruttó</TH>
                    <TH align="right">Ebből magán</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.byUser.map((u) => (
                    <TR key={u.userId}>
                      <TD>{u.userName ?? '—'}</TD>
                      <TD numeric>{u.count}</TD>
                      <TD numeric className="font-semibold">
                        {moneyCompact(u.gross, 'HUF')}
                      </TD>
                      <TD numeric className="text-amber-700 dark:text-amber-300">
                        {u.paidPersonal > 0 ? moneyCompact(u.paidPersonal, 'HUF') : '—'}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>

          {/* Cég vs magán */}
          <Card>
            <CardHeader>
              <CardTitle>Fizetésmód szerint</CardTitle>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-x divide-slate-200 dark:divide-slate-700">
              <PaidByCell label="Céges" count={data.byPaidBy.company.count} gross={data.byPaidBy.company.gross} tone="emerald" />
              <PaidByCell label="Magán" count={data.byPaidBy.personal.count} gross={data.byPaidBy.personal.gross} tone="amber" />
              <PaidByCell label="Nincs jelölve" count={data.byPaidBy.unset.count} gross={data.byPaidBy.unset.gross} tone="slate" />
            </div>
          </Card>

          {/* Trend grafikon (12 hónap) */}
          {trendQ.data && trendQ.data.months.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Trend (utolsó 12 hónap, bruttó)
                  </span>
                </CardTitle>
              </CardHeader>
              <div className="p-4 text-slate-700 dark:text-slate-200">
                <BarChart
                  data={trendQ.data.months.map((m) => ({
                    label: `${String(m.year).slice(-2)}.${String(m.month).padStart(2, '0')}`,
                    value: m.gross,
                  }))}
                  height={220}
                  format={(v) => (v >= 1_000_000 ? (v / 1_000_000).toFixed(1) + 'M' : v >= 1000 ? Math.round(v / 1000) + 'k' : String(Math.round(v)))}
                  emptyHint="Még nincs adat — adj hozzá számlákat."
                />
              </div>
            </Card>
          )}

          {/* Projekt profitability — kimenő (bevétel) vs bejövő (költség) per projekt */}
          {trendQ.data && trendQ.data.projectProfitability.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" /> Projekt profitabilitás (utolsó 12 hónap)
                  </span>
                </CardTitle>
              </CardHeader>
              <Table>
                <THead>
                  <TR>
                    <TH>Projekt</TH>
                    <TH align="right">Bevétel</TH>
                    <TH align="right">Költség</TH>
                    <TH align="right">Profit</TH>
                    <TH align="right">Margin</TH>
                  </TR>
                </THead>
                <TBody>
                  {trendQ.data.projectProfitability.map((p) => (
                    <TR key={p.projectCode}>
                      <TD>
                        <span className="font-mono text-sm">{p.projectCode}</span>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">
                          {p.revenueCount} bevétel · {p.costCount} költség
                        </div>
                      </TD>
                      <TD numeric className="text-emerald-700 dark:text-emerald-300">
                        {moneyCompact(p.revenue, 'HUF')}
                      </TD>
                      <TD numeric className="text-slate-700 dark:text-slate-300">
                        {moneyCompact(p.cost, 'HUF')}
                      </TD>
                      <TD numeric className={p.profit >= 0 ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'font-semibold text-red-700 dark:text-red-300'}>
                        {moneyCompact(p.profit, 'HUF')}
                      </TD>
                      <TD numeric>
                        {p.margin != null ? (
                          <span className={p.margin >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}>
                            {p.margin.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}

          {/* Top beszállítók (utolsó 12 hónap) */}
          {trendQ.data && trendQ.data.topSuppliers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <Trophy className="h-4 w-4" /> Top beszállítók (utolsó 12 hónap)
                  </span>
                </CardTitle>
              </CardHeader>
              <Table>
                <THead>
                  <TR>
                    <TH>Beszállító</TH>
                    <TH>Adószám</TH>
                    <TH align="right">Számla db</TH>
                    <TH align="right">Bruttó összesen</TH>
                  </TR>
                </THead>
                <TBody>
                  {trendQ.data.topSuppliers.map((s, i) => (
                    <TR key={`${s.name}-${i}`}>
                      <TD>
                        <span className="font-medium">
                          {i + 1}. {s.name}
                        </span>
                      </TD>
                      <TD>
                        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                          {s.taxNumber ?? '—'}
                        </span>
                      </TD>
                      <TD numeric>{s.count}</TD>
                      <TD numeric className="font-semibold">
                        {moneyCompact(s.gross, 'HUF')}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}

          {/* Kategória bontás (donut) */}
          {trendQ.data && trendQ.data.categories.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span className="inline-flex items-center gap-2">
                    <Tag className="h-4 w-4" /> Kategóriák (utolsó 12 hónap)
                  </span>
                </CardTitle>
              </CardHeader>
              <div className="p-4 text-slate-700 dark:text-slate-200">
                <DonutChart
                  data={trendQ.data.categories.slice(0, 10).map((c, i) => ({
                    label: c.category ?? '— nincs kategória —',
                    value: c.gross,
                    color: CHART_COLORS[i % CHART_COLORS.length],
                  }))}
                  size={220}
                  emptyHint="Még nincs kategorizált számla."
                />
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

const CHART_COLORS = [
  '#2563eb', // brand-600
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#f97316', // orange-500
  '#6b7280', // gray-500
]

function SummaryCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub: string
  tone?: 'amber' | 'emerald'
}) {
  const toneClass =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-900/40'
      : tone === 'emerald'
        ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-900/40'
        : 'border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700'
  return (
    <div className={`rounded-lg border ${toneClass} p-4`}>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
    </div>
  )
}

function PaidByCell({
  label,
  count,
  gross,
  tone,
}: {
  label: string
  count: number
  gross: number
  tone: 'emerald' | 'amber' | 'slate'
}) {
  const toneClass =
    tone === 'amber'
      ? 'text-amber-700 dark:text-amber-300'
      : tone === 'emerald'
        ? 'text-emerald-700 dark:text-emerald-300'
        : 'text-slate-700 dark:text-slate-300'
  return (
    <div className="p-4">
      <div className={`text-xs font-medium ${toneClass}`}>{label}</div>
      <div className="mt-1 text-xl font-semibold">{moneyCompact(gross, 'HUF')}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{count} db</div>
    </div>
  )
}
